/**
 * The conditional-branching carousel: an equation, a one-of-N condition selector, and whatever the
 * block wants to author per branch.
 *
 * Seven blocks built this. The six content blocks were ~95% identical — the differences were two
 * spellings of the same error predicate, two of the same toolbar release, and Ordering's row
 * grouping. GPS had drifted much further, and not only in styling: it had no armed delete (one
 * click destroyed a branch and its feedback), no branch-jump hand-off from its test bar, and no
 * focus restoration after a delete. Three missing behaviours is what turned "fix GPS" into "there
 * should only be one of these".
 *
 * The carousel owns the CONDITION and nothing else. Each block's per-branch payload — Dropdown's
 * role map, Ordering's canonical order, GPS's coordinates — arrives as `children`, so no block's
 * data shape ever reaches this file. That split already existed implicitly in all seven; naming it
 * is what makes one component possible.
 *
 * `activeIndex` is a prop rather than internal state because GPS shares it with its Capture and
 * Paste controls, which write into whichever branch is currently showing.
 */
import {ChevronLeft, ChevronRight, Plus, Trash2} from 'lucide-react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';


import { FlowPickerMenu } from './flow-picker-menu';
import { FormulaField } from './formula-field';

import type { TaskVariable } from '@/types/experience';
import type { AngleMode } from '@agavi/blocks';

import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { IconButton } from '@/components/ui/icon-button';
import { NumberField } from '@/components/ui/number-field';
import SelectMenu from '@/components/ui/select-menu';

/** Only the comparison. Whatever else a block hangs off a branch is its own business. */
export interface CarouselBranch {
  id: string;
  operator: '<' | '<=' | '==' | '>=' | '>' | 'between';
  threshold: string;
  thresholdHigh?: string;
}

/*
 * `= < ≤ > ≥ between`, the order every other comparison picker in the app uses — numerical, slider
 * and angle each declare it twice, and Flow's attempt condition follows them. This file was the one
 * dissenter, running `< ≤ = ≥ >`; reordering the options changes no stored data, since the value is
 * the operator string and only its position in the list moves.
 *
 * `between` stays a WORD rather than becoming `↔`. Among five mathematical symbols a word reads as
 * a deliberate sixth option instead of a symbol somebody forgot to supply.
 */
const OPERATORS = ['==', '<', '<=', '>', '>=', 'between'] as const;
const OPERATOR_SYMBOL: Record<string, string> = { '<': '<', '<=': '≤', '==': '=', '>=': '≥', '>': '>' };



export interface BranchConditionCarouselProps {
  equation: string;
  onEquationChange: (v: string) => void;
  variables: TaskVariable[];
  rolledValues: Record<string, string>;
  angleMode?: AngleMode;
  /** Paints the equation field's own error state. */
  equationInvalid?: boolean;
  /** False while the equation is missing or unresolvable — everything it governs goes inert. */
  equationWorks: boolean;
  /** Paints the wrapper border: a missing/invalid equation, or a coverage gap. */
  hasError?: boolean;

  branches: CarouselBranch[];
  activeIndex: number;
  onActiveIndexChange: (updater: (i: number) => number) => void;
  onBranchChange: (id: string, patch: Partial<CarouselBranch>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;

  /** Releases the shared rich-text toolbar when a plain field takes focus. */
  onFieldFocus?: () => void;
  /**
   * The single variable named in the question, if there is exactly one. Written into the equation
   * on mount when it is still empty.
   *
   * The carousel mounts as the direct result of ticking "Allow alternative answer setups", and the
   * toggle only exists because the question already holds a chip — so the teacher has told us what
   * they are branching on before we ask. Retyping it was busywork. Blocks pass nothing when the
   * question names several variables: there is no confident guess between them, and the
   * placeholder explains the field instead.
   */
  seedVariable?: string;

  children?: React.ReactNode;
}

/**
 * The prev button is the ref target: after a branch is deleted the block moves focus here rather
 * than letting it fall to <body>, which is why this forwards a ref at all.
 */
export const BranchConditionCarousel = forwardRef<HTMLButtonElement, BranchConditionCarouselProps>(
  function BranchConditionCarousel({
    equation, onEquationChange, variables, rolledValues, angleMode,
    equationInvalid, equationWorks, hasError, seedVariable,
    branches, activeIndex, onActiveIndexChange, onBranchChange, onAdd, onRemove,
    onFieldFocus, children,
  }, prevButtonRef) {
    const { t } = useTranslation();
    const [armedId, setArmedId] = useState<string | null>(null);

    // Mount is the signal: this component appears exactly when branching is switched on, so there
    // is no need for any block to tell it that the toggle was just ticked.
    const seededRef = useRef(false);
    const startedEmpty = useRef(!equation.trim());
    useEffect(() => {
      if (seededRef.current) return;
      seededRef.current = true;
      if (seedVariable && !equation.trim()) onEquationChange(`{${seedVariable}}`);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clamped = Math.min(activeIndex, Math.max(0, branches.length - 1));
    const active = branches[clamped];
    if (!active) return null;

    const isLast = clamped === branches.length - 1;
    const armed = armedId === active.id;

    // Two numbers that cross over are a contradiction the teacher can see in place, so both fields
    // carry the error rather than the row or the wrapper.
    const lo = parseFloat(active.threshold);
    const hi = parseFloat(active.thresholdHigh ?? '');
    const inverted = active.operator === 'between' && Number.isFinite(lo) && Number.isFinite(hi) && lo > hi;

    const thresholdField = (
      key: 'threshold' | 'thresholdHigh',
      ariaLabel: string,
      width: string,
      invalid = false,
    ) => (
      <NumberField
        // `scientific`, matching the FormulaField above it: that equation can resolve to 6.02e23,
        // and under `decimal` the threshold you compare it against silently became 6.0223 — the
        // exponent stripped, twenty-two orders of magnitude wrong, with no warning anywhere.
        grammar='scientific'
        allowNegative
        value={(key === 'threshold' ? active.threshold : active.thresholdHigh) ?? ''}
        onChange={v => onBranchChange(active.id, { [key]: v })}
        hasError={invalid}
        ariaLabel={ariaLabel}
        onFocus={onFieldFocus}
        className={`${width} min-w-0 flex-1`}
      />
    );

    return (
      <div className={`flex flex-col gap-3 rounded-lg border p-4 ${hasError ? 'border-ws-error-fg' : 'border-ws-border'}`}>
        {/* No visible label — the placeholder says what the field is for, which is more use than
            naming it "Equation". The aria-label keeps it named for assistive tech, where a
            placeholder is not a reliable accessible name. */}
        <FormulaField
          fieldId='conditionalEquation'
          value={equation}
          onChange={onEquationChange}
          variables={variables}
          rolledValues={rolledValues}
          ariaLabel={t('blocks.branching.equationLabel')}
          placeholder={t('blocks.branching.equationPlaceholder')}
          hideEval
          hasError={equationInvalid}
          angleMode={angleMode}
          className='w-full'
          onFocus={onFieldFocus}
          focusOnMount={startedEmpty.current}
      />

        {/* The gate covers the conditions AND the per-branch payload. The six blocks were split
            3-3 on whether `children` belonged inside it; inside wins because a half-dimmed box
            reads as a rendering fault rather than a state, and because the one control that can
            get you out — the equation, directly above — stays live either way. */}
        <div
          {...(!equationWorks ? { inert: '' } : {})}
          // The showing branch, so an undo of a branch edit lands on the branch rather than the
          // whole block. Only one branch is mounted at a time; the carousel registers the rest
          // with `registerFieldReveal`, which is what pages a hidden one into view first.
          data-field-id={active.id}
          className={`flex flex-col gap-3 transition-opacity ${!equationWorks ? 'pointer-events-none opacity-30' : 'opacity-100'}`}
        >
          <div className='flex flex-col gap-1.5'>
            {/* Navigation on its own row, so the operator/threshold/delete row below always has
                room regardless of how many inputs 'between' needs. */}
            <div className='flex items-center justify-center gap-1.5'>
              <IconButton
                ref={prevButtonRef}
                type='button'
                size='sm'
                aria-label={t('blocks.branching.previousCondition')}
                disabled={clamped === 0}
                onClick={() => onActiveIndexChange(i => Math.max(0, i - 1))}
              >
                <ChevronLeft className='h-4 w-4' />
              </IconButton>
              <span className='shrink-0 text-xs font-medium tabular-nums text-ws-muted' aria-live='polite'>
                {/* Position only. The comparison belongs on the FEEDBACK cycler, where the
                    operator/threshold controls are not visible — here they are directly below,
                    so restating them is the same fact twice. */}
                {t('blocks.branching.conditionIndicator', { current: clamped + 1, total: branches.length })}
              </span>
              {/*
                * THE END OF THE TRACK IS A MENU, like the scenario pager's and the task
                * stepper's. It was the worst of the three: a bare `+` that created a condition
                * and left you standing on the old one, so the only evidence anything happened
                * was the total ticking up. One press, two surprises.
                *
                * One option, deliberately — the menu earns its place by what it PREVENTS, not
                * by what it offers. And adding now MOVES you to what you just made.
                */}
              {isLast ? (
                <FlowPickerMenu
                  sections={[{ options: [{ key: 'add', text: t('blocks.branching.addCondition') }] }]}
                  onChoose={() => { onAdd(); onActiveIndexChange(() => branches.length); }}
                  ariaLabel={t('blocks.branching.addCondition')}
                  noMatchesLabel={t('blocks.branching.addCondition')}
                  renderTrigger={p => (
                    <IconButton
                      ref={p.ref}
                      type='button'
                      size='sm'
                      onClick={e => { e.stopPropagation(); p.onClick(); }}
                      aria-haspopup={p['aria-haspopup']}
                      aria-expanded={p['aria-expanded']}
                      aria-label={t('blocks.branching.addCondition')}
                    >
                      <Plus className='h-4 w-4' />
                    </IconButton>
                  )}
                />
              ) : (
                <IconButton
                  type='button'
                  size='sm'
                  aria-label={t('blocks.branching.nextCondition')}
                  onClick={() => onActiveIndexChange(i => Math.min(branches.length - 1, i + 1))}
                >
                  <ChevronRight className='h-4 w-4' />
                </IconButton>
              )}
            </div>

            {/* Armed-delete highlight on THIS row only — the nav row above has nothing to do with
                the delete button and shouldn't light up as if the whole carousel were going. */}
            <div className={`flex items-center gap-1.5 ${
              armed ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''
            }`}>
              {/* Native, deliberately: six symbols need no rich content, and native buys keyboard
                  navigation, type-ahead and the OS wheel picker on phone. `appearance-none` plus
                  our own chevron is what makes the closed control look like the rest of the app;
                  the open popup stays the platform's. */}
              <div className='relative shrink-0'>
                {/* The app's own dropdown, not a native select: this sits in a row of Agavi
                    controls, and a native one opens the operating system's menu, which reads as a
                    different kind of control in the middle of a block editor. */}
                <SelectMenu fieldId={`${active.id}.operator`}
                  value={active.operator}
                  onChange={(v: string) => onBranchChange(active.id, { operator: v as CarouselBranch['operator'] })}
                  options={OPERATORS.map(op => ({
                    value: op,
                    label: op === 'between' ? t('blocks.branching.operatorBetween') : (OPERATOR_SYMBOL[op] ?? op),
                  }))}
                  ariaLabel={t('blocks.branching.operatorLabel')}
                  fullWidth
                />
              </div>

              {active.operator === 'between' ? (
                <>
                  {thresholdField('threshold', t('blocks.branching.thresholdLowLabel'), 'w-16', inverted)}
                  <span aria-hidden='true' className='shrink-0 select-none text-xs text-ws-muted'>{t('blocks.connectors.to')}</span>
                  {thresholdField('thresholdHigh', t('blocks.branching.thresholdHighLabel'), 'w-16', inverted)}
                </>
              ) : (
                thresholdField('threshold', t('blocks.branching.thresholdLabel'), 'w-20')
              )}

              <IconButton
                type='button'
                variant='destructive'
                size='sm'
                isArmed={armed}
                disabled={branches.length <= 1}
                aria-label={armed ? t('blocks.branching.confirmRemoveCondition') : t('blocks.branching.removeCondition')}
                onClick={() => { if (armed) { onRemove(active.id); setArmedId(null); } else setArmedId(active.id); }}
                onPointerLeave={e => { if (e.pointerType !== 'touch' && armed) setArmedId(null); }}
                onBlur={() => { if (armed) setArmedId(null); }}
                onKeyDown={e => { if (e.key === 'Escape' && armed) { e.stopPropagation(); setArmedId(null); } }}
              >
                <Trash2 className='h-4 w-4' />
              </IconButton>
            </div>
          </div>

          {/* A little more air than the column's own `gap-3`. The condition above is a bordered
              field and most blocks open their answers with another one, so at the bare gap two
              boxes nearly touch and read as one control. GPS looked right at `gap-3` only because
              its first child is a text label rather than a box; the extra space costs it nothing
              and saves the other six. */}
          <div className='mt-1'>{children}</div>
        </div>
      </div>
    );
  }
);
